import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

import API_BASE_URL from "../config/api";


function EditSubject() {

    const { id } = useParams();

    const navigate = useNavigate();


    /*
    |--------------------------------------------------------------------------
    | Form Data
    |--------------------------------------------------------------------------
    */

    const [formData, setFormData] = useState({
        subject_name: "",
        status: "Enable"
    });


    /*
    |--------------------------------------------------------------------------
    | Loading States
    |--------------------------------------------------------------------------
    */

    const [loading, setLoading] = useState(true);

    const [saving, setSaving] = useState(false);


    /*
    |--------------------------------------------------------------------------
    | Get Subject Details
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        const fetchSubject = async () => {

            try {

                const response = await fetch(
                    `${API_BASE_URL}/admin/subjects/${id}`,
                    {
                        method: "GET",

                        headers: {
                            "Accept": "application/json"
                        },

                        credentials: "include"
                    }
                );


                const data = await response.json();


                if (response.ok && data.success) {

                    setFormData({
                        subject_name: data.subject?.subject_name || "",
                        status: data.subject?.status || "Enable"
                    });

                } else {

                    Swal.fire({
                        icon: "error",
                        title: "Unable to Load Subject",
                        text: data.message || "Subject not found."
                    });

                    navigate("/subjects");
                }

            } catch (error) {

                console.error("Fetch Subject Error:", error);

                Swal.fire({
                    icon: "error",
                    title: "Server Error",
                    text: "Unable to connect with server."
                });

                navigate("/subjects");

            } finally {

                setLoading(false);
            }
        };


        fetchSubject();

    }, [id, navigate]);


    /*
    |--------------------------------------------------------------------------
    | Handle Input Change
    |--------------------------------------------------------------------------
    */

    const handleChange = (e) => {

        const { name, value } = e.target;

        setFormData({
            ...formData,
            [name]: value
        });
    };


    /*
    |--------------------------------------------------------------------------
    | Update Subject
    |--------------------------------------------------------------------------
    */

    const handleSubmit = async (e) => {

        e.preventDefault();


        if (!formData.subject_name.trim()) {

            Swal.fire({
                icon: "warning",
                title: "Subject Name Required",
                text: "Please enter subject name."
            });

            return;
        }


        try {

            setSaving(true);


            const response = await fetch(
                `${API_BASE_URL}/admin/subjects/${id}`,
                {
                    method: "PUT",

                    headers: {
                        "Accept": "application/json",
                        "Content-Type": "application/json"
                    },

                    credentials: "include",

                    body: JSON.stringify(formData)
                }
            );


            const data = await response.json();


            if (response.ok && data.success) {

                await Swal.fire({
                    icon: "success",
                    title: "Updated!",
                    text: data.message || "Subject updated successfully.",
                    timer: 1500,
                    showConfirmButton: false
                });


                navigate("/subjects");

            } else {

                Swal.fire({
                    icon: "error",
                    title: "Unable to Update Subject",
                    text: data.message || "Something went wrong."
                });
            }

        } catch (error) {

            console.error("Update Subject Error:", error);

            Swal.fire({
                icon: "error",
                title: "Server Error",
                text: "Unable to connect with server."
            });

        } finally {

            setSaving(false);
        }
    };


    /*
    |--------------------------------------------------------------------------
    | Loading Screen
    |--------------------------------------------------------------------------
    */

    if (loading) {

        return (

            <div className="container-fluid">

                <div className="text-center py-5">

                    <div
                        className="spinner-border text-primary"
                        role="status"
                    >
                    </div>

                    <p className="mt-2">
                        Loading subject...
                    </p>

                </div>

            </div>
        );
    }


    /*
    |--------------------------------------------------------------------------
    | UI
    |--------------------------------------------------------------------------
    */

    return (

        <div className="container-fluid">

            {/* Page Header */}

            <div className="d-flex justify-content-between align-items-center mb-4">

                <h3 className="mb-0">
                    Edit Subject
                </h3>

                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => navigate("/subjects")}
                >
                    Back
                </button>

            </div>


            {/* Form Card */}

            <div className="card shadow-sm">

                <div className="card-body">

                    <form onSubmit={handleSubmit}>

                        {/* Subject Name */}

                        <div className="mb-3">

                            <label className="form-label">
                                Subject Name
                            </label>

                            <input
                                type="text"
                                name="subject_name"
                                className="form-control"
                                placeholder="Enter subject name"
                                value={formData.subject_name}
                                onChange={handleChange}
                                maxLength="100"
                            />

                        </div>


                        {/* Status */}

                        <div className="mb-3">

                            <label className="form-label">
                                Status
                            </label>

                            <select
                                name="status"
                                className="form-select"
                                value={formData.status}
                                onChange={handleChange}
                            >
                                <option value="Enable">
                                    Enable
                                </option>

                                <option value="Disable">
                                    Disable
                                </option>

                            </select>

                        </div>


                        {/* Buttons */}

                        <div className="d-flex gap-2">

                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={saving}
                            >
                                {saving ? "Updating..." : "Update Subject"}
                            </button>


                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => navigate("/subjects")}
                                disabled={saving}
                            >
                                Cancel
                            </button>

                        </div>

                    </form>

                </div>

            </div>

        </div>
    );
}


export default EditSubject;